import type { Draft } from "immer";
import { produce } from "immer";
import invariant from "invariant";
import { get, pull } from "lodash";
import type { Topic } from "../../../services/datastore";
import { getTopicTypeName } from "../../../utils";
import type { PlayerRecord } from "../types";
import type {
  ContainerNode,
  InitializedPanelNode,
  LayoutNode,
  PanelNode,
  UninitializedPanelNode,
} from "./api";
import type { SplitOrientation } from "./constants";
import {
  IMAGE_ROTATION_STEP_MAGNITUDE_DEG,
  MAX_CHART_FIELDS,
  RotationDirection,
  VisualizationType,
} from "./constants";
import { supportsImage, supportsMap, walkLayoutTree } from "./utils";

type LayoutState = { root: LayoutNode; nextId: number };

/**
 * Split a panel, creating a new, uninitialized sibling panel. The orientation
 * describes if the sibling should be to the right of or below the base panel.
 */
export type SplitPanelAction = {
  type: "split-panel";
  payload: {
    panelId: PanelNode["id"];
    orientation: SplitOrientation;
  };
};

export function splitPanel(
  panelId: PanelNode["id"],
  orientation: SplitOrientation
): SplitPanelAction {
  return {
    type: "split-panel",
    payload: {
      panelId,
      orientation,
    },
  };
}

/**
 * Remove a panel from the tree entirely. If the panel is the only one in the
 * tree, a new, uninitialized panel replaces it.
 */
export type RemovePanelAction = {
  type: "remove-panel";
  payload: {
    panelId: PanelNode["id"];
  };
};

export function removePanel(panelId: PanelNode["id"]): RemovePanelAction {
  return {
    type: "remove-panel",
    payload: {
      panelId,
    },
  };
}

/**
 * Resize a panel or container. The flex describes its relative size compared
 * to its sibling.
 */
export type ResizeNodeAction = {
  type: "resize-node";
  payload: {
    nodeId: LayoutNode["id"];
    flex: number;
  };
};

export function resizeNode(
  nodeId: LayoutNode["id"],
  flex: number
): ResizeNodeAction {
  return {
    type: "resize-node",
    payload: {
      nodeId,
      flex,
    },
  };
}

/**
 * Load a layout, overwriting the existing layout completely. Useful to load
 * layout profiles kept in a user's web storage.
 */
export type LoadLayoutAction = {
  type: "load-layout";
  payload: {
    layout: LayoutNode;
  };
};

export function loadLayout(layout: LayoutNode): LoadLayoutAction {
  return {
    type: "load-layout",
    payload: {
      layout,
    },
  };
}

/**
 * Initialize the panel using the given topic. The panel's visualization-related
 * fields will be given default values. The initial visualization depends on the
 * topic's message type, if any:
 *  - If the message type supports image data, the panel will be initialized
 *    to the "image" visualization
 *  - If the message type supports GPS data, the panel will be initialized to
 *    the "map" visualization
 *  - For all other message types, the panel will be initialized to the
 *    "timeline" visualization
 *
 * Invariants:
 *  - Panel must be uninitialized
 */
export type SelectTopicAction = {
  type: "select-topic";
  payload: {
    panelId: PanelNode["id"];
    topic: Topic;
  };
};

export function selectTopic(
  panelId: PanelNode["id"],
  topic: Topic
): SelectTopicAction {
  return {
    type: "select-topic",
    payload: {
      panelId,
      topic,
    },
  };
}

/**
 * Shows or hides the controls for the image visualization component.
 *
 * Invariants:
 *  - Panel must be initialized
 */
export type ToggleImageControlsAction = {
  type: "toggle-image-controls";
  payload: {
    panelId: PanelNode["id"];
  };
};

export function toggleImageControls(
  panelId: PanelNode["id"]
): ToggleImageControlsAction {
  return {
    type: "toggle-image-controls",
    payload: {
      panelId,
    },
  };
}

/**
 * Rotates the displayed image 90 degrees in the specified direction
 *
 * Invariants:
 *  - Panel must be initialized
 */
export type RotateImageAction = {
  type: "rotate-image";
  payload: {
    panelId: PanelNode["id"];
    direction: RotationDirection;
  };
};

export function rotateImage(
  panelId: PanelNode["id"],
  direction: RotationDirection
): RotateImageAction {
  return {
    type: "rotate-image",
    payload: {
      panelId,
      direction,
    },
  };
}

/**
 * Adds a field to be plotted in the chart visualization. The field can be
 * top-level or nested and must be available in the `messageData` payload
 * argument. The field must follow the syntax for lodash's `get` method. The
 * value at the given field should be a numeric type.
 *
 * There are some circumstances under which the field will not be added, though
 * these circumstances do not represent an error:
 *  - Maximum number of fields already added
 *  - Field has already been added
 *  - Field value is not a numeric type
 *
 * Invariants:
 *  - Panel must be initialized
 */
export type AddChartFieldAction = {
  type: "add-chart-field";
  payload: {
    panelId: PanelNode["id"];
    field: string;
    messageData: PlayerRecord["messageData"];
  };
};

export function addChartField(
  panelId: PanelNode["id"],
  field: string,
  messageData: PlayerRecord["messageData"]
): AddChartFieldAction {
  return {
    type: "add-chart-field",
    payload: {
      panelId,
      field,
      messageData,
    },
  };
}

/**
 * Removes the given field from the panel's chart visualization fields.
 *
 * Invariants:
 *  - Panel must be initialized
 *  - Field must have previously been added
 */
export type RemoveChartFieldAction = {
  type: "remove-chart-field";
  payload: {
    panelId: PanelNode["id"];
    field: string;
  };
};

export function removeChartField(
  panelId: PanelNode["id"],
  field: string
): RemoveChartFieldAction {
  return {
    type: "remove-chart-field",
    payload: {
      panelId,
      field,
    },
  };
}

/**
 * Selects the provided tag for the panel, unless that tag is already selected,
 * in which case the tag will be un-selected.
 *
 * Invariants:
 *   - Panel must be initialized
 */
export type SelectTagAction = {
  type: "select-tag";
  payload: {
    panelId: PanelNode["id"];
    tag: string;
  };
};

export function selectTag(
  panelId: PanelNode["id"],
  tag: string
): SelectTagAction {
  return {
    type: "select-tag",
    payload: {
      panelId,
      tag,
    },
  };
}

/**
 * Switches the panel to the given visualization. To choose the "chart"
 * visualization, at least one field must have already been selected.
 *
 * Invariants:
 *  - Panel must be initialized
 *  - If `visualization === "chart"`, must be > 0 fields added
 */
export type ChooseVisualizationAction = {
  type: "choose-visualization";
  payload: {
    panelId: PanelNode["id"];
    tab: VisualizationType;
  };
};

export function chooseVisualization(
  panelId: PanelNode["id"],
  tab: VisualizationType
): ChooseVisualizationAction {
  return {
    type: "choose-visualization",
    payload: {
      panelId,
      tab,
    },
  };
}

/**
 * Clears the selected topic for the panel, returning the panel to an
 * uninitialized state.
 *
 * Invariants:
 *  - Panel must be initialized
 */
export type ChooseNewTopicAction = {
  type: "choose-new-topic";
  payload: {
    panelId: PanelNode["id"];
  };
};

export function chooseNewTopic(panelId: PanelNode["id"]): ChooseNewTopicAction {
  return {
    type: "choose-new-topic",
    payload: {
      panelId,
    },
  };
}

export type PanelLayoutAction =
  | SplitPanelAction
  | RemovePanelAction
  | ResizeNodeAction
  | SelectTopicAction
  | ToggleImageControlsAction
  | RotateImageAction
  | AddChartFieldAction
  | RemoveChartFieldAction
  | SelectTagAction
  | ChooseVisualizationAction
  | ChooseNewTopicAction
  | LoadLayoutAction;

export const initialState: LayoutState = {
  root: makeUninitializedPanel(0, null, 1),
  nextId: 1,
};

export default function reducer(
  draft: Draft<LayoutState>,
  action: PanelLayoutAction
) {
  switch (action.type) {
    case "split-panel": {
      const panel = getPanel(draft, action.payload.panelId);

      const panelParentId = panel.parentId;

      const siblingId = draft.nextId++;
      const newParentId = draft.nextId++;

      const sibling = makeUninitializedPanel(siblingId, newParentId, 0.5);

      const newParent: ContainerNode = {
        type: "container",
        parentId: panelParentId,
        id: newParentId,
        flex: panel.flex,
        orientation: action.payload.orientation,
        firstChild: panel,
        secondChild: sibling,
      };

      panel.parentId = newParentId;
      panel.flex = 0.5;

      replaceNode(draft, panelParentId, panel, newParent);

      return;
    }
    case "remove-panel": {
      const panel = getPanel(draft, action.payload.panelId);

      const panelParentId = panel.parentId;

      if (panelParentId === null) {
        draft.root = makeUninitializedPanel(draft.nextId++, null, 1);
      } else {
        const parent = getContainer(draft, panelParentId);

        const sibling =
          panel === parent.firstChild ? parent.secondChild : parent.firstChild;

        replaceNode(draft, parent.parentId, parent, sibling);

        sibling.parentId = parent.parentId;

        if (parent.parentId === null) {
          sibling.flex = 1;
        } else {
          sibling.flex = parent.flex;
        }
      }

      return;
    }
    case "resize-node": {
      const node = getNode(draft, action.payload.nodeId);

      node.flex = action.payload.flex;

      return;
    }
    case "load-layout": {
      let maxId = action.payload.layout.id;

      draft.root = produce(action.payload.layout, (draftLayout) => {
        walkLayoutTree(draftLayout, (node) => {
          maxId = Math.max(maxId, node.id);
        });
      });

      draft.nextId = maxId + 1;

      return;
    }
    case "select-topic": {
      const panel = getUninitializedPanel(draft, action.payload.panelId);

      const initializedPanel = makeInitializedPanel(
        panel.id,
        panel.parentId,
        panel.flex,
        action.payload.topic
      );

      replaceNode(draft, initializedPanel.parentId, panel, initializedPanel);

      return;
    }
    case "toggle-image-controls": {
      const panel = getInitializedPanel(draft, action.payload.panelId);

      panel.showImageControls = !panel.showImageControls;

      return;
    }
    case "rotate-image": {
      const panel = getInitializedPanel(draft, action.payload.panelId);

      const rotateByDeg =
        action.payload.direction === RotationDirection.Left
          ? -IMAGE_ROTATION_STEP_MAGNITUDE_DEG
          : IMAGE_ROTATION_STEP_MAGNITUDE_DEG;

      panel.imageRotationDeg += rotateByDeg;

      return;
    }
    case "add-chart-field": {
      const panel = getInitializedPanel(draft, action.payload.panelId);

      const { field, messageData } = action.payload;

      if (panel.fields.length >= MAX_CHART_FIELDS) {
        return;
      }

      if (panel.fields.includes(field)) {
        return;
      }

      if (typeof get(messageData, field) !== "number") {
        return;
      }

      panel.fields.push(field);

      return;
    }
    case "remove-chart-field": {
      const panel = getInitializedPanel(draft, action.payload.panelId);

      const { field } = action.payload;

      invariant(
        panel.fields.includes(field),
        `Field not currently selected: "${field}"`
      );

      pull(panel.fields, field);

      return;
    }
    case "select-tag": {
      const panel = getInitializedPanel(draft, action.payload.panelId);

      if (panel.selectedTag === action.payload.tag) {
        panel.selectedTag = null;
      } else {
        panel.selectedTag = action.payload.tag;
      }

      return;
    }
    case "choose-visualization": {
      const panel = getInitializedPanel(draft, action.payload.panelId);

      invariant(
        action.payload.tab !== "chart" || panel.fields.length > 0,
        "Must have at least 1 field selected to show chart"
      );

      panel.visualization = action.payload.tab;

      return;
    }
    case "choose-new-topic": {
      const panel = getInitializedPanel(draft, action.payload.panelId);

      const uninitializedPanel = makeUninitializedPanel(
        panel.id,
        panel.parentId,
        panel.flex
      );

      replaceNode(
        draft,
        uninitializedPanel.parentId,
        panel,
        uninitializedPanel
      );

      return;
    }
    default: {
      const _exhaustiveCheck: never = action;
      throw new Error(`Received unknown action: ${_exhaustiveCheck}`);
    }
  }
}

// Helpers

function makeUninitializedPanel(
  id: PanelNode["id"],
  parentId: PanelNode["parentId"],
  flex: PanelNode["flex"]
): UninitializedPanelNode {
  return {
    type: "panel",
    parentId,
    id,
    flex,
    isInitialized: false,
  };
}

function makeInitializedPanel(
  id: PanelNode["id"],
  parentId: PanelNode["parentId"],
  flex: PanelNode["flex"],
  topic: Topic
): InitializedPanelNode {
  const topicTypeName = getTopicTypeName(topic);

  let initialVisualization: VisualizationType;
  if (supportsImage(topicTypeName)) {
    initialVisualization = VisualizationType.Image;
  } else if (supportsMap(topicTypeName)) {
    initialVisualization = VisualizationType.Map;
  } else {
    initialVisualization = VisualizationType.Timeline;
  }

  return {
    type: "panel",
    id,
    parentId,
    flex,
    isInitialized: true,
    topicName: topic.name,
    topicTypeName,
    visualization: initialVisualization,
    fields: [],
    imageRotationDeg: 0,
    showImageControls: false,
    selectedTag: null,
  };
}

function replaceNode(
  draft: Draft<LayoutState>,
  parentId: LayoutNode["parentId"],
  currentNode: LayoutNode,
  replacementNode: LayoutNode
): void {
  if (parentId === null) {
    draft.root = replacementNode;
  } else {
    const parent = getContainer(draft, parentId);

    if (currentNode === parent.firstChild) {
      parent.firstChild = replacementNode;
    } else {
      parent.secondChild = replacementNode;
    }
  }
}

function getUninitializedPanel(
  draft: Draft<LayoutState>,
  panelId: PanelNode["id"]
): UninitializedPanelNode {
  const panel = getPanel(draft, panelId);

  invariant(
    !panel.isInitialized,
    `Panel with ID ${panelId} is already initialized`
  );

  return panel;
}

function getInitializedPanel(
  draft: Draft<LayoutState>,
  panelId: PanelNode["id"]
): InitializedPanelNode {
  const panel = getPanel(draft, panelId);

  invariant(panel.isInitialized, `Panel with ID ${panelId} is not initialized`);

  return panel;
}

function getPanel(
  draft: Draft<LayoutState>,
  nodeId: PanelNode["id"]
): PanelNode {
  const node = getNode(draft, nodeId);

  invariant(node.type === "panel", `Node with ID ${nodeId} is not a panel`);

  return node;
}

function getContainer(
  draft: Draft<LayoutState>,
  nodeId: ContainerNode["id"]
): ContainerNode {
  const node = getNode(draft, nodeId);

  invariant(
    node.type === "container",
    `Node with ID ${nodeId} is not a container`
  );

  return node;
}

function getNode(
  draft: Draft<LayoutState>,
  nodeId: LayoutNode["id"]
): LayoutNode {
  let maybeNode: LayoutNode | undefined = undefined;

  function visitor(node: LayoutNode) {
    if (node.id === nodeId) {
      maybeNode = node;
      return false;
    }
  }

  walkLayoutTree(draft.root, visitor);

  invariant(maybeNode !== undefined, `Node with ID ${nodeId} does not exist`);

  return maybeNode;
}
